import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'

import {
  IconOptionsPlaylist,
  IconOptionsReport,
  IconOptionsThrash
} from 'assets/svg/sprite'
import { addToast } from 'components/Toast/Toast'
import AddToPlaylist from 'components/UI/Modal/AddToPlaylist/AddToPlaylist'
import Report from 'components/UI/Modal/Report/Report'
import { useModalContext } from 'context/modalContext'
import { useReportPost } from 'helpers/hooks'
import { deleteSingleWatchedPost } from 'services/endpoints/media'
import { type IOption } from 'types/iTypes'

import styles from './mediaItemOptions.module.scss'

type PlaylistItemType = 'post' | 'message'

interface Props {
  role: string
  type: string
  isOpen: boolean
  itemId: string
  itemType: PlaylistItemType
  setIsOpen: (_value: boolean) => void
}

export const MediaItemOptions = ({
  role,
  type,
  isOpen,
  itemId,
  itemType,
  setIsOpen
}: Props) => {
  const modalContext = useModalContext()
  const queryClient = useQueryClient()
  const { reportPostMutation } = useReportPost()
  const { t } = useTranslation()

  const closeOptions = () => {
    setIsOpen(false)
  }

  const deleteWatchedPostMutate = useMutation(
    async () => await deleteSingleWatchedPost(itemId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['getMediaData'])
        closeOptions()
      },
      onError: () => {
        addToast('error', t('error:errorSomethingWentWrong'))
      }
    }
  )

  const handlePostReport = () => {
    if (itemType !== 'post') {
      return
    }

    modalContext.addModal(
      t('reportPost'),
      <Report
        type='content'
        confirmFn={(message: string, reportType: number) => {
          reportPostMutation.mutate(
            {
              postId: itemId,
              message,
              reportType
            },
            {
              onSuccess: () => {
                modalContext.clearModal()
              }
            }
          )
        }}
        closeFn={() => {
          modalContext.clearModal()
        }}
      />
    )
  }

  const handleAddToPlaylist = () => {
    closeOptions()

    modalContext.addModal(
      '',
      <AddToPlaylist
        itemId={itemId}
        itemType={itemType}
        closeFn={() => {
          modalContext.clearModal()
        }}
      />,
      true,
      true
    )
  }

  const getOptionsArray = (
    optionType: string
  ): IOption[] | null => {
    if (optionType === 'purchase') {
      return [
        {
          icon: <IconOptionsPlaylist />,
          text: t('addToList'),
          disabled: false,
          action: handleAddToPlaylist
        }
      ]
    }

    if (optionType === 'playlists') {
      return [
        {
          icon: <IconOptionsPlaylist />,
          text: t('addToList'),
          disabled: false,
          action: handleAddToPlaylist
        },
        {
          icon: <IconOptionsThrash />,
          text: t('removeFromWatchHistory'),
          disabled: false,
          action: () => {
            console.log('Should hide from purchases')
            closeOptions()
          }
        }
      ]
    }

    if (optionType === 'watch-history') {
      return [
        {
          icon: <IconOptionsPlaylist />,
          text: t('addToList'),
          disabled: false,
          action: handleAddToPlaylist
        },
        {
          icon: <IconOptionsThrash />,
          text: t('removeFromWatchHistory'),
          disabled: false,
          action: () => {
            deleteWatchedPostMutate.mutate()
          }
        }
      ]
    }

    if (optionType === 'likes') {
      return [
        {
          icon: <IconOptionsPlaylist />,
          text: t('addToList'),
          disabled: false,
          action: handleAddToPlaylist
        }
      ]
    }

    return null
  }

  const options = getOptionsArray(type)

  if (!itemId || !isOpen) {
    return null
  }

  return (
    <div
      className={`${styles.container} ${
        isOpen ? styles.active : ''
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={styles.section}>
        {options?.map((opt: IOption, key: number) => (
          <div
            key={key}
            className={`${styles.option} ${
              opt.disabled ? styles.disabled : ''
            }`}
            onClick={() => {
              if (opt.disabled) {
                return
              }

              opt.action(itemId)
            }}
          >
            {opt.icon}
            <span>{opt.text}</span>
          </div>
        ))}
      </div>

      <div className={styles.separator}></div>

      <div
        className={`${styles.section} ${styles.sectionBottom}`}
      >
        {role === 'owner' ? (
          <div className={styles.option}>
            <IconOptionsThrash />
            <span>{t('deletePost')}</span>
          </div>
        ) : itemType === 'post' ? (
          <div
            className={styles.option}
            onClick={() => {
              closeOptions()
              handlePostReport()
            }}
          >
            <IconOptionsReport />
            <span>{t('reportPost')}</span>
          </div>
        ) : null}
      </div>
    </div>
  )
}